<script lang="ts">
  // Screen-space ATK/DEF + level labels for the DOM duel board (flat AND tilted).
  //
  // In-card labels lived INSIDE the perspective plane: the projection sheared and shrank the far
  // rows' text, and preserve-3d depth-sorting let neighbouring cards paint over them. This layer
  // instead renders flat 2D labels in the FIXED viewport space and glues each one to its monster's
  // PROJECTED card rect: getBoundingClientRect on a 3D-transformed element returns the projected
  // screen box, so one rAF read/write loop keeps every label pinned through tilt, levitation,
  // shake and flights — always upright, always the same pixel size for near and far rows.
  import { duel, mrefKey } from "../../stores/duel.svelte.ts";
  import { POS, type QueriedCard } from "@ygoevo/protocol";

  interface Mon { key: string; card: QueriedCard }

  const fmt = (v: number | undefined | null) => (v === undefined || v === null ? "" : v === -2 ? "?" : String(v));
  const lvlText = (c: QueriedCard): string =>
    c.link ? `LINK-${c.link}` : c.rank ? `R${c.rank}` : c.level ? `L${c.level}` : "";

  // Every face-up monster in a monster zone (main + EMZ), either side of the table.
  const monsters = $derived.by((): Mon[] => {
    const b = duel.board;
    if (!b) return [];
    const out: Mon[] = [];
    for (const p of b.players)
      for (const c of p.mzone) {
        if (!c || !c.code) continue;
        if ((c.position & POS.FACEDOWN) !== 0) continue;
        if (c.type === undefined || (c.type & 0x1) === 0) continue; // monsters only
        out.push({ key: mrefKey(c.controller, c.location, c.sequence), card: c });
      }
    return out;
  });

  let layer = $state<HTMLDivElement | null>(null);

  // Position pass: reads (projected anchor rects) then writes (label transforms), imperative so the
  // per-frame work never re-renders the Svelte tree. Anchors are the zones' [data-mref] wrappers.
  function place(): void {
    if (!layer) return;
    for (const el of layer.children as unknown as HTMLElement[]) {
      const key = el.dataset.for ?? "";
      const anchor = document.querySelector<HTMLElement>(`[data-mref="${CSS.escape(key)}"]`);
      // hide with its card: mid-flight zones set inline visibility:hidden on the anchor
      if (!anchor || anchor.style.visibility === "hidden") { el.style.display = "none"; continue; }
      const r = anchor.getBoundingClientRect();
      if (r.width < 4) { el.style.display = "none"; continue; }
      el.style.display = "";
      el.style.translate = `${r.left + r.width / 2}px ${r.top}px`;
      el.style.height = `${r.height}px`;
      el.style.fontSize = `${Math.max(9, Math.min(15, r.width * 0.16))}px`;
    }
  }

  $effect(() => {
    void monsters; // re-place immediately when the set changes (new label mounts at 0,0 otherwise)
    if (typeof requestAnimationFrame !== "function") { place(); return; } // jsdom
    let raf = 0;
    const loop = () => { place(); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  });
</script>

<!-- fixed viewport layer: above the board (stage stacking context), below prompts/menus/HUD -->
<div bind:this={layer} class="pointer-events-none fixed inset-0 z-[24]" aria-hidden="true">
  {#each monsters as m (m.key)}
    {@const lvl = lvlText(m.card)}
    <div class="stat-label absolute left-0 top-0" data-for={m.key} style="display:none">
      {#if lvl}<div class="stat-text absolute bottom-full left-1/2 -translate-x-1/2 pb-[0.15em] text-[0.78em] opacity-90">{lvl}</div>{/if}
      <div class="stat-text absolute top-full left-1/2 -translate-x-1/2 pt-[0.12em]">{fmt(m.card.attack)} / {fmt(m.card.defense)}</div>
    </div>
  {/each}
</div>

<style>
  /* the wrapper is a zero-width line pinned to the projected rect's top-centre; children hang off
     its top/bottom edges (level above the card, stats below) */
  .stat-label { width: 0; }
  /* Readability over ANY card art (owner asked twice): a solid dark backing pill guarantees contrast,
     a crisp multi-directional BLACK ring keeps every glyph edge defined even where the pill is thin. */
  .stat-text {
    font-family: var(--font-display);
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    white-space: nowrap;
    padding: 0.12em 0.42em;
    border-radius: 0.32em;
    background: rgba(3, 5, 10, 0.82);
    color: #fff;
    -webkit-text-stroke: 0.055em #000;
    paint-order: stroke fill;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.55), 0 1px 3px rgba(0, 0, 0, 0.7);
    text-shadow:
      0.05em 0 0 #000, -0.05em 0 0 #000, 0 0.05em 0 #000, 0 -0.05em 0 #000,
      0.04em 0.04em 0 #000, -0.04em 0.04em 0 #000, 0.04em -0.04em 0 #000, -0.04em -0.04em 0 #000;
  }
</style>
